import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getCfoResearch } from "@/api/cfoWork";
import { getCfoScoreExplanation } from "@/api/cfoWorkflow";
import type { CfoResearchBrief, CfoScoreExplanation } from "@/api/types";

export interface CfoResearchData {
  brief: CfoResearchBrief;
  /** Null when the company has no current evaluation to explain. */
  explanation: CfoScoreExplanation | null;
}

/** One read per company of a lot, shared by the card and the prefetch of the
 *  next company, so moving on shows the next sheet at once. */
export interface CfoResearchCache {
  load: (companyId: number) => Promise<CfoResearchData>;
  prefetch: (companyId: number) => void;
  /** Forget a company, e.g. once it was re-scored. */
  forget: (companyId: number) => void;
}

export function useCfoResearchCache(slug: string): CfoResearchCache {
  const entries = useRef(new Map<number, Promise<CfoResearchData>>());
  useEffect(() => {
    entries.current = new Map();
  }, [slug]);

  const load = useCallback(
    (companyId: number) => {
      let entry = entries.current.get(companyId);
      if (!entry) {
        entry = Promise.all([
          getCfoResearch(slug, companyId),
          getCfoScoreExplanation(slug, companyId).catch(() => null),
        ]).then(([brief, explanation]) => ({ brief, explanation }));
        // A failed read is retried on the next request, not cached.
        entry.catch(() => entries.current.delete(companyId));
        entries.current.set(companyId, entry);
      }
      return entry;
    },
    [slug],
  );

  const prefetch = useCallback(
    (companyId: number) => {
      void load(companyId).catch(() => undefined);
    },
    [load],
  );

  const forget = useCallback((companyId: number) => {
    entries.current.delete(companyId);
  }, []);

  return useMemo(() => ({ load, prefetch, forget }), [load, prefetch, forget]);
}

export interface CfoResearchState {
  data: CfoResearchData | null;
  failed: boolean;
  reload: () => void;
}

/** The research sheet and score explanation of one company, from the cache. */
export function useCfoResearch(cache: CfoResearchCache, companyId: number | null): CfoResearchState {
  const [data, setData] = useState<CfoResearchData | null>(null);
  const [failed, setFailed] = useState(false);
  const [generation, setGeneration] = useState(0);
  const shown = useRef<number | null>(null);

  useEffect(() => {
    if (companyId === null) return;
    let live = true;
    // Another company starts blank; a reload of the same one keeps its sheet
    // on screen until the fresh one arrives.
    if (shown.current !== companyId) setData(null);
    shown.current = companyId;
    setFailed(false);
    cache
      .load(companyId)
      .then((value) => {
        if (live) setData(value);
      })
      .catch(() => {
        if (live) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, [cache, companyId, generation]);

  const reload = useCallback(() => {
    if (companyId !== null) cache.forget(companyId);
    setGeneration((n) => n + 1);
  }, [cache, companyId]);

  return { data, failed, reload };
}
